import React from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { NorthIndianSvgChart } from './ChartViewer';
import { getNakshatraName, getGenderName, getZodiacName, getPlanetFullName, getTithiName, getVaraName } from '../utils/language';
import { formatDOB } from '../utils/dateUtils';
import { Printer, ArrowLeft, Star, Award, Layers, Sparkles, BookOpen, ShieldCheck, Compass, Activity, FileText } from 'lucide-react';
import { SwastikLogo } from './SwastikLogo';

interface PrintReportProps {
  kundali: CompleteKundali;
  onBack: () => void;
  lang?: Language;
}

export const PrintReport: React.FC<PrintReportProps> = ({ kundali, onBack, lang = 'en' }) => {
  const activeLang: Language = lang === 'hi' ? 'hi' : 'en';
  const {
    birthDetails,
    panchang,
    avakhada,
    planets,
    houses,
    charts,
    shadbala,
    charaKarakas,
    avasthas,
    ashtakvarga,
    dashas,
    manglik,
    kaalSarp,
    sadeSati,
    pitraDosha,
    gemstones
  } = kundali;

  const currentDasha = dashas.find((d) => d.isCurrent) || dashas[0];
  const moonPlanet = planets.find((p) => p.name === 'Moon');
  const lifeStone = Array.isArray(gemstones) ? (gemstones.find((g) => g.type?.includes('Life')) || gemstones[0]) : null;
  const luckyStone = Array.isArray(gemstones) ? (gemstones.find((g) => g.type?.includes('Lucky')) || gemstones[1]) : null;
  const fortuneStone = Array.isArray(gemstones) ? (gemstones.find((g) => g.type?.includes('Fortune')) || gemstones[2]) : null;

  // List of all 16 Shodashvarga charts for the PDF report
  const vargaList = [
    { code: 'D1', name: 'Lagna (D1)', title: 'Rashi / Birth Chart', desc: 'Physical Body, Health & Life Path', data: charts.D1 },
    { code: 'Chandra', name: 'Chandra Chart', title: 'Moon Chart', desc: 'Mind, Emotions & Perception', data: charts.Chandra || charts.D1 },
    { code: 'Surya', name: 'Surya Chart', title: 'Sun Chart', desc: 'Soul, Authority & Power', data: charts.Surya || charts.D1 },
    { code: 'D2', name: 'Hora (D2)', title: 'Wealth & Finances', desc: 'Liquid Assets & Prosperity', data: charts.D2 || charts.D1 },
    { code: 'D3', name: 'Drekkana (D3)', title: 'Siblings & Courage', desc: 'Valour, Initiative & Relatives', data: charts.D3 || charts.D1 },
    { code: 'D4', name: 'Chaturthamsha (D4)', title: 'Property & Assets', desc: 'Fixed Assets, Land & Happiness', data: charts.D4 || charts.D1 },
    { code: 'D7', name: 'Saptamsha (D7)', title: 'Children & Progeny', desc: 'Lineage, Legacy & Offspring', data: charts.D7 || charts.D1 },
    { code: 'D9', name: 'Navamsha (D9)', title: 'Spouse & Destiny', desc: 'Marriage, Dharma & Spiritual Fortune', data: charts.D9 || charts.D1 },
    { code: 'D10', name: 'Dashamsha (D10)', title: 'Career & Fame', desc: 'Profession, Power & Social Status', data: charts.D10 || charts.D1 },
    { code: 'D12', name: 'Dwadasamsha (D12)', title: 'Parents & Lineage', desc: 'Ancestral Karma & Parents', data: charts.D12 || charts.D1 },
    { code: 'D16', name: 'Shodashamsha (D16)', title: 'Vehicles & Luxury', desc: 'Conveyances & Domestic Comforts', data: charts.D16 || charts.D1 },
    { code: 'D20', name: 'Vimshamsha (D20)', title: 'Spiritual Progress', desc: 'Upasana, Devotion & Meditation', data: charts.D20 || charts.D1 },
    { code: 'D24', name: 'Chaturvimshamsha (D24)', title: 'Knowledge & Learning', desc: 'Higher Education & Wisdom', data: charts.D24 || charts.D1 },
    { code: 'D27', name: 'Saptavimshamsha (D27)', title: 'Strengths & Weaknesses', desc: 'Physical Vitality & Stamina', data: charts.D27 || charts.D1 },
    { code: 'D30', name: 'Trishamsha (D30)', title: 'Misfortunes & Health', desc: 'Evils, Obstacles & Afflictions', data: charts.D30 || charts.D1 },
    { code: 'D40', name: 'Khavedamsha (D40)', title: 'Auspicious Effects', desc: 'Maternal Line & Auspiciousness', data: charts.D40 || charts.D1 },
    { code: 'D45', name: 'Akshavedamsha (D45)', title: 'Character & Aptitude', desc: 'Paternal Line & General Wellbeing', data: charts.D45 || charts.D1 },
    { code: 'D60', name: 'Shashtiamsha (D60)', title: 'Past Life Karma', desc: 'All Aspects of Life & Subliminal Karma', data: charts.D60 || charts.D1 }
  ];

  return (
    <div className="min-h-screen bg-astro-card text-astro-text p-4 sm:p-8 space-y-6 print:bg-white print:text-black print:p-0">
      {/* Top Floating Control Bar (Hidden when printing) */}
      <div className="max-w-5xl mx-auto flex items-center justify-between bg-astro-bg border border-astro-border p-4 rounded-2xl shadow-sm print:hidden">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-astro-card border border-astro-border hover:text-astro-primary text-xs font-semibold text-astro-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-astro-primary" />
          <span>Back to App</span>
        </button>

        <div className="text-xs text-astro-primary font-semibold font-serif flex items-center gap-2">
          <SwastikLogo size="sm" />
          <span>Vedanga Jyotisha Detailed PDF Report</span>
          <span className="text-astro-secondary-text">• {birthDetails.name}</span>
        </div>

        <button
          onClick={() => window.print()}
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-astro-primary hover:bg-[#123d3c] text-white font-bold text-xs shadow-md transition-all transform hover:scale-[1.02]"
        >
          <Printer className="w-4 h-4 text-astro-accent" />
          <span>Print / Save Detailed PDF</span>
        </button>
      </div>

      {/* Printable Report Paper Layout Container */}
      <div className="max-w-5xl mx-auto bg-astro-bg border border-astro-border rounded-3xl p-6 sm:p-10 shadow-sm space-y-10 print:bg-white print:text-black print:p-0 print:border-none print:shadow-none print:rounded-none">
        
        {/* SECTION 1: HEADER & NATIVE BIRTH PROFILE */}
        <div className="space-y-6">
          <div className="text-center border-b border-astro-border pb-6 print:border-astro-border flex flex-col items-center">
            <SwastikLogo size="lg" className="mb-2" />
            <div className="text-3xl font-bold font-serif text-astro-primary print:text-astro-secondary">
              {activeLang === 'hi' ? 'वेदांग ज्योतिषम्' : 'Vedang Jyotisham'}
            </div>
            <div className="text-xs font-bold tracking-widest uppercase text-astro-primary/90 mt-1 print:text-astro-secondary">
              Celestial Science
            </div>
            <div className="text-[10px] text-astro-secondary-text mt-1 print:text-astro-secondary-text">
              Calculated using Precision Sidereal Lahiri Ephemeris & Parashari Principles
            </div>
          </div>

          {/* Native Details & Panchang Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            {/* Birth Details Box */}
            <div className="bg-astro-card p-4 rounded-2xl border border-astro-border space-y-2 print:bg-astro-secondary-surface print:border-astro-border">
              <h3 className="font-bold text-astro-primary font-serif uppercase tracking-wider text-xs border-b border-astro-border pb-1.5 print:text-astro-secondary print:border-astro-border">
                Native Birth Profile
              </h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{activeLang === 'hi' ? 'पूरा नाम' : 'Full Name'}</span>
                  <span className="font-bold text-astro-text text-sm print:text-astro-text">{birthDetails.name}</span>
                </div>
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{activeLang === 'hi' ? 'लिंग' : 'Gender'}</span>
                  <span className="font-semibold text-astro-primary print:text-astro-secondary uppercase">{getGenderName(birthDetails.gender, activeLang)}</span>
                </div>
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{activeLang === 'hi' ? 'जन्म तिथि व समय' : 'Date & Time'}</span>
                  <span className="font-semibold text-astro-text print:text-astro-text">
                    {formatDOB(birthDetails.date)} at {birthDetails.time}
                  </span>
                </div>
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{activeLang === 'hi' ? 'जन्म स्थान' : 'Place of Birth'}</span>
                  <span className="font-semibold text-astro-text print:text-astro-text">{birthDetails.placeName}</span>
                </div>
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{activeLang === 'hi' ? 'अक्षांश / देशांतर' : 'Coordinates'}</span>
                  <span className="font-mono text-[11px] text-astro-secondary-text print:text-astro-secondary-text">
                    {birthDetails.latitude}° N, {birthDetails.longitude}° E
                  </span>
                </div>
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{activeLang === 'hi' ? 'अयनांश' : 'Ayanamsha'}</span>
                  <span className="font-semibold text-astro-primary print:text-astro-secondary">Chitra Paksha / Lahiri</span>
                </div>
              </div>
            </div>

            {/* Panchang & Avakhada Box */}
            <div className="bg-astro-card p-4 rounded-2xl border border-astro-border space-y-2 print:bg-astro-secondary-surface print:border-astro-border">
              <h3 className="font-bold text-astro-primary font-serif uppercase tracking-wider text-xs border-b border-astro-border pb-1.5 print:text-astro-secondary print:border-astro-border">
                {activeLang === 'hi' ? 'पंचांग एवं अवकहड़ा चक्र' : 'Panchang & Avakhada Chakra'}
              </h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{activeLang === 'hi' ? 'लग्न / राशि' : 'Lagna / Rashi'}</span>
                  <span className="font-bold text-astro-primary print:text-astro-secondary">
                    {getZodiacName(houses[0].sign, activeLang)} / {getZodiacName(moonPlanet?.sign || houses[0].sign, activeLang)}
                  </span>
                </div>
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{activeLang === 'hi' ? 'नक्षत्र (पद)' : 'Nakshatra (Pada)'}</span>
                  <span className="font-bold text-astro-text print:text-astro-text">
                    {getNakshatraName(panchang.nakshatra, activeLang)} ({activeLang === 'hi' ? `पद ${moonPlanet?.pada || 1}` : `Pada ${moonPlanet?.pada || 1}`})
                  </span>
                </div>
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{activeLang === 'hi' ? 'तिथि व वार' : 'Tithi & Vaar'}</span>
                  <span className="font-semibold text-astro-text print:text-astro-text">
                    {getTithiName(panchang.tithi, activeLang)} • {getVaraName(panchang.vara, activeLang)}
                  </span>
                </div>
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{lang === 'hi' ? 'योग व करण' : 'Yoga & Karana'}</span>
                  <span className="font-semibold text-astro-text print:text-astro-text">
                    {panchang.yoga} • {panchang.karana}
                  </span>
                </div>
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{lang === 'hi' ? 'गण व नाड़ी' : 'Gana & Nadi'}</span>
                  <span className="font-semibold text-astro-text print:text-astro-text">
                    {avakhada.gana} • {avakhada.nadi}
                  </span>
                </div>
                <div>
                  <span className="text-astro-secondary-text block text-[10px] uppercase font-bold print:text-astro-muted">{lang === 'hi' ? 'वर्ण व योनि' : 'Varna & Yoni'}</span>
                  <span className="font-semibold text-astro-text print:text-astro-text">
                    {avakhada.varna} • {avakhada.yoni}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: CORE KUNDALI CHARTS (D1, Chandra, D9) */}
        <div className="space-y-4">
          <h3 className="font-bold text-astro-primary text-sm font-serif uppercase tracking-wider border-b border-astro-border pb-2 print:text-astro-secondary print:border-amber-800">
            {lang === 'hi' ? 'मुख्य त्रि-कुंडली अवलोकन' : 'Core Tri-Kundali Overview'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3">
            {/* D1 Lagna */}
            <div className="bg-astro-card p-3 rounded-2xl border border-astro-border flex flex-col items-center print:bg-white print:border-astro-border">
              <h4 className="font-bold text-astro-primary text-xs mb-1 font-serif uppercase tracking-wider print:text-astro-text">
                {lang === 'hi' ? 'D1 - लग्न कुंडली' : 'D1 - Lagna Kundali (Rashi)'}
              </h4>
              <p className="text-[10px] text-astro-secondary-text mb-2 print:text-astro-muted">{lang === 'hi' ? 'जन्म चार्ट एवं लग्न' : 'Birth Chart & Ascendant'}</p>
              <div className="w-full aspect-square max-w-[240px]">
                <NorthIndianSvgChart chartData={charts.D1} planets={planets} onSelectPlanet={() => {}} lang={lang} />
              </div>
            </div>

            {/* Chandra Chart */}
            <div className="bg-astro-card p-3 rounded-2xl border border-astro-border flex flex-col items-center print:bg-white print:border-astro-border">
              <h4 className="font-bold text-astro-primary text-xs mb-1 font-serif uppercase tracking-wider print:text-astro-text">
                {lang === 'hi' ? 'चन्द्र कुंडली' : 'Chandra Kundali'}
              </h4>
              <p className="text-[10px] text-astro-secondary-text mb-2 print:text-astro-muted">{lang === 'hi' ? 'चंद्र लग्न व मन' : 'Moon Ascendant & Mind'}</p>
              <div className="w-full aspect-square max-w-[240px]">
                <NorthIndianSvgChart
                  chartData={charts.Chandra || charts.D1}
                  planets={(charts.Chandra || charts.D1).planets}
                  onSelectPlanet={() => {}}
                  lang={lang}
                />
              </div>
            </div>

            {/* D9 Navamsha */}
            <div className="bg-astro-card p-3 rounded-2xl border border-astro-border flex flex-col items-center print:bg-white print:border-astro-border">
              <h4 className="font-bold text-astro-primary text-xs mb-1 font-serif uppercase tracking-wider print:text-astro-text">
                {lang === 'hi' ? 'D9 - नवमांश कुंडली' : 'D9 - Navamsha Kundali'}
              </h4>
              <p className="text-[10px] text-astro-secondary-text mb-2 print:text-astro-muted">Spouse, Dharma & Destiny</p>
              <div className="w-full aspect-square max-w-[240px]">
                <NorthIndianSvgChart
                  chartData={charts.D9}
                  planets={charts.D9.planets}
                  onSelectPlanet={() => {}}
                  lang={lang}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 3: FULL SHODASHVARGA (16 DIVISIONAL CHARTS GALLERY) */}
        <div className="space-y-4 print:break-before-page">
          <div className="border-b border-astro-border pb-2 print:border-amber-800">
            <h3 className="font-bold text-astro-primary text-sm font-serif uppercase tracking-wider print:text-astro-secondary">
              Shodashvarga (Complete 16 Divisional Charts Gallery)
            </h3>
            <p className="text-xs text-astro-secondary-text print:text-astro-secondary-text">
              Comprehensive Parashari Varga divisional charts mapping specific domains of life
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 print:grid-cols-3 print:gap-3">
            {vargaList.map((v) => (
              <div
                key={v.code}
                className="bg-astro-card p-3 rounded-2xl border border-astro-border flex flex-col items-center space-y-1.5 print:bg-white print:border-astro-border"
              >
                <div className="text-center">
                  <h4 className="font-bold text-astro-primary text-xs font-serif print:text-astro-secondary">{v.name}</h4>
                  <p className="text-[9px] text-astro-secondary-text print:text-astro-secondary-text line-clamp-1">{v.title}</p>
                </div>
                <div className="w-full aspect-square max-w-[200px]">
                  <NorthIndianSvgChart
                    chartData={v.data}
                    planets={v.data.planets}
                    onSelectPlanet={() => {}}
                  />
                </div>
                <p className="text-[9px] text-astro-secondary-text text-center print:text-astro-muted line-clamp-1">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 4: SHATBAL (SHADBALA) & BHAVBAL (BHAVA BALA) ANALYTICS */}
        {shadbala && (
          <div className="space-y-6 print:break-before-page">
            <div className="border-b border-astro-border pb-2 print:border-amber-800">
              <h3 className="font-bold text-astro-primary text-sm font-serif uppercase tracking-wider print:text-astro-secondary">
                Shatbal & Bhava Bala Strength Analytics
              </h3>
              <p className="text-xs text-astro-secondary-text print:text-astro-secondary-text">
                Parashari 6-Fold Planetary Strengths & 12 House Potency Metrics
              </p>
            </div>

            {/* Shatbal Bar Chart Analytics */}
            <div className="bg-astro-card p-4 rounded-2xl border border-astro-border space-y-3 print:bg-astro-secondary-surface print:border-astro-border">
              <h4 className="font-bold text-astro-primary text-xs font-serif uppercase tracking-wider print:text-astro-text">
                Planetary Shatbal Strength Ratio Graph (Virupas / Rupas)
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                {shadbala.planets.map((p) => {
                  const pct = Math.min(Math.round(p.ratio * 100), 200);
                  return (
                    <div key={p.planet} className="p-2.5 bg-astro-bg rounded-xl border border-astro-border space-y-1.5 print:bg-white print:border-astro-border">
                      <div className="flex justify-between items-center text-[11px]">
                        <span className="font-bold text-astro-text print:text-astro-text">{p.planet}</span>
                        <span className="font-mono text-astro-primary font-bold print:text-astro-secondary">
                          {p.totalRupas} R ({p.ratio}x Req)
                        </span>
                      </div>
                      <div className="w-full h-2.5 bg-astro-card rounded-full overflow-hidden border border-astro-border print:bg-astro-card print:border-astro-border">
                        <div
                          className="h-full bg-astro-primary print:bg-amber-700 rounded-full"
                          style={{ width: `${Math.min(pct / 2, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[9px] text-astro-secondary-text print:text-astro-muted">
                        <span>Category: {p.strengthCategory}</span>
                        <span>Sthana: {p.sthanaBala} | Dig: {p.digBala}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Shatbal Data Table */}
            <div className="overflow-x-auto border border-astro-border rounded-2xl print:border-astro-border">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-astro-card text-astro-secondary-text uppercase text-[10px] tracking-wider print:bg-astro-secondary-surface print:text-astro-secondary-text">
                    <th className="py-2 px-3">Planet</th>
                    <th className="py-2 px-3">Sthana</th>
                    <th className="py-2 px-3">Dig</th>
                    <th className="py-2 px-3">Kaala</th>
                    <th className="py-2 px-3">Cheshta</th>
                    <th className="py-2 px-3">Naisargika</th>
                    <th className="py-2 px-3">Drik</th>
                    <th className="py-2 px-3">Total Rupas</th>
                    <th className="py-2 px-3">Req Rupas</th>
                    <th className="py-2 px-3">Ratio</th>
                    <th className="py-2 px-3">Category</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-astro-border text-astro-text print:divide-slate-300 print:text-astro-text">
                  {shadbala.planets.map((p) => (
                    <tr key={p.planet}>
                      <td className="py-2 px-3 font-bold text-astro-primary print:text-astro-secondary">{p.planet}</td>
                      <td className="py-2 px-3 font-mono">{p.sthanaBala}</td>
                      <td className="py-2 px-3 font-mono">{p.digBala}</td>
                      <td className="py-2 px-3 font-mono">{p.kaalaBala}</td>
                      <td className="py-2 px-3 font-mono">{p.cheshtaBala}</td>
                      <td className="py-2 px-3 font-mono">{p.naisargikaBala}</td>
                      <td className="py-2 px-3 font-mono">{p.drikBala}</td>
                      <td className="py-2 px-3 font-mono font-bold">{p.totalRupas} R</td>
                      <td className="py-2 px-3 font-mono text-astro-secondary-text print:text-astro-secondary-text">{p.requiredRupas} R</td>
                      <td className="py-2 px-3 font-mono font-bold text-astro-primary print:text-astro-secondary">{p.ratio}</td>
                      <td className="py-2 px-3 font-semibold">{p.strengthCategory}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bhava Bala 12 House Table */}
            <div className="bg-astro-card p-4 rounded-2xl border border-astro-border space-y-3 print:bg-astro-secondary-surface print:border-astro-border">
              <h4 className="font-bold text-astro-primary text-xs font-serif uppercase tracking-wider print:text-astro-text">
                12 Bhava Bala (House Strengths Breakdown)
              </h4>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 text-xs">
                {shadbala.bhavaBala.map((bh) => (
                  <div key={bh.houseNumber} className="p-2.5 bg-astro-bg rounded-xl border border-astro-border text-xs print:bg-white print:border-astro-border">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-astro-primary print:text-astro-secondary">House {bh.houseNumber}</span>
                      <span className="font-mono font-bold text-astro-text print:text-astro-text">{bh.totalRupas} R</span>
                    </div>
                    <div className="text-[10px] text-astro-secondary-text print:text-astro-secondary-text mt-1">
                      Sign: {bh.sign} ({bh.signLord})
                    </div>
                    <div className="text-[9px] font-semibold text-astro-primary mt-0.5 print:text-astro-secondary">
                      Status: {bh.category}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SECTION 5: PLANETARY POSITIONS, JAIMINI KARAKAS & ASHTAKVARGA */}
        <div className="space-y-6 print:break-before-page">
          <div className="border-b border-astro-border pb-2 print:border-amber-800">
            <h3 className="font-bold text-astro-primary text-sm font-serif uppercase tracking-wider print:text-astro-secondary">
              Planetary Longitudes, Dignities & Jaimini Karakas
            </h3>
          </div>

          {/* Planetary Table */}
          <div className="overflow-x-auto border border-astro-border rounded-2xl print:border-astro-border">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-astro-card text-astro-secondary-text uppercase text-[10px] tracking-wider print:bg-astro-secondary-surface print:text-astro-secondary-text">
                  <th className="py-2.5 px-3">Planet</th>
                  <th className="py-2.5 px-3">Sign</th>
                  <th className="py-2.5 px-3">Degree</th>
                  <th className="py-2.5 px-3">House</th>
                  <th className="py-2.5 px-3">Nakshatra</th>
                  <th className="py-2.5 px-3">Pada</th>
                  <th className="py-2.5 px-3">Dignity</th>
                  <th className="py-2.5 px-3">Motion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border text-astro-text print:divide-slate-300 print:text-astro-text">
                {planets.map((p) => (
                  <tr key={p.name}>
                    <td className="py-2 px-3 font-bold text-astro-primary print:text-astro-secondary">{p.name}</td>
                    <td className="py-2 px-3 font-semibold">{p.sign}</td>
                    <td className="py-2 px-3 font-mono">{p.degreeFormatted}</td>
                    <td className="py-2 px-3">H{p.house}</td>
                    <td className="py-2 px-3">{p.nakshatra}</td>
                    <td className="py-2 px-3">{p.pada}</td>
                    <td className="py-2 px-3 font-semibold">{p.dignity}</td>
                    <td className="py-2 px-3">{p.isRetrograde ? 'Retrograde (R)' : 'Direct'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Jaimini Chara Karakas & Avasthas Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            {/* Chara Karakas */}
            <div className="bg-astro-card p-4 rounded-2xl border border-astro-border space-y-2 print:bg-astro-secondary-surface print:border-astro-border">
              <h3 className="font-bold text-astro-primary font-serif uppercase tracking-wider text-xs border-b border-astro-border pb-1.5 print:text-astro-secondary print:border-astro-border">
                Jaimini 7 Chara Karakas
              </h3>
              <div className="space-y-1.5">
                {charaKarakas?.map((k) => (
                  <div key={k.code} className="flex justify-between items-center text-xs border-b border-astro-border pb-1 print:border-astro-border">
                    <span className="font-bold text-astro-text print:text-astro-text">
                      {k.code} - {k.name} ({k.planet})
                    </span>
                    <span className="font-mono text-astro-primary print:text-astro-secondary">{k.formattedDegree}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sarvashtakvarga SAV Summary */}
            <div className="bg-astro-card p-4 rounded-2xl border border-astro-border space-y-2 print:bg-astro-secondary-surface print:border-astro-border">
              <h3 className="font-bold text-astro-primary font-serif uppercase tracking-wider text-xs border-b border-astro-border pb-1.5 print:text-astro-secondary print:border-astro-border">
                Sarvashtakvarga (SAV Points Matrix)
              </h3>
              <div className="grid grid-cols-4 gap-2 text-center font-mono text-xs">
                {ashtakvarga?.sarvashtakvarga.map((pts, idx) => (
                  <div key={idx} className="p-2 bg-astro-bg rounded-xl border border-astro-border print:bg-white print:border-astro-border">
                    <span className="text-[10px] text-astro-secondary-text block print:text-astro-muted">House {idx + 1}</span>
                    <span className={`font-bold ${pts >= 28 ? 'text-emerald-600 print:text-emerald-700' : 'text-rose-600 print:text-rose-700'}`}>
                      {pts} pts
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 6: VIMSHOTTARI DASHA & REMEDIES */}
        <div className="space-y-6 print:break-before-page">
          <div className="border-b border-astro-border pb-2 print:border-amber-800">
            <h3 className="font-bold text-astro-primary text-sm font-serif uppercase tracking-wider print:text-astro-secondary">
              Vimshottari Dasha Timeline & Remedies
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            {/* Current Dasha Box */}
            <div className="bg-astro-card p-4 rounded-2xl border border-astro-border space-y-2 print:bg-astro-secondary-surface print:border-astro-border">
              <h3 className="font-bold text-astro-primary font-serif uppercase tracking-wider text-xs border-b border-astro-border pb-1.5 print:text-astro-secondary print:border-astro-border">
                Active Dasha Period
              </h3>
              <div className="space-y-1">
                <p className="text-sm font-bold text-astro-text print:text-astro-text">
                  {currentDasha.planet} Mahadasha
                </p>
                <p className="text-xs text-astro-primary print:text-astro-secondary font-semibold">
                  {currentDasha.startDate} to {currentDasha.endDate} ({currentDasha.durationYears} Years)
                </p>
                <div className="mt-3 space-y-1 border-t border-astro-border pt-2 print:border-astro-border">
                  <span className="text-[10px] uppercase font-bold text-astro-secondary-text print:text-astro-muted">Antardashas (Sub-Periods):</span>
                  <div className="grid grid-cols-2 gap-1 text-[11px] font-mono mt-1">
                    {currentDasha.antardashas?.slice(0, 6).map((sub) => (
                      <div key={sub.planet} className="p-1 bg-astro-bg rounded border border-astro-border print:bg-white print:border-astro-border">
                        {sub.planet}: {sub.startDate}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Dosha Analysis Box */}
            <div className="bg-astro-card p-4 rounded-2xl border border-astro-border space-y-2 print:bg-astro-secondary-surface print:border-astro-border">
              <h3 className="font-bold text-astro-primary font-serif uppercase tracking-wider text-xs border-b border-astro-border pb-1.5 print:text-astro-secondary print:border-astro-border">
                Key Dosha Status & Gemstones
              </h3>
              <div className="space-y-1.5 text-xs">
                <div>
                  <span className="font-bold text-astro-text print:text-astro-text">Manglik Dosha: </span>
                  <span className="text-astro-primary print:text-astro-secondary font-semibold">{manglik.summary}</span>
                </div>
                <div>
                  <span className="font-bold text-astro-text print:text-astro-text">Sade Sati: </span>
                  <span className="text-astro-primary print:text-astro-secondary font-semibold">{sadeSati.phase}</span>
                </div>
                <div>
                  <span className="font-bold text-astro-text print:text-astro-text">Kaal Sarp: </span>
                  <span className="text-astro-primary print:text-astro-secondary font-semibold">{kaalSarp.type}</span>
                </div>

                <div className="mt-3 border-t border-astro-border pt-2 space-y-1 print:border-astro-border">
                  <span className="text-[10px] uppercase font-bold text-astro-secondary-text print:text-astro-muted">Recommended Gemstones:</span>
                  <div className="grid grid-cols-3 gap-1.5 text-center mt-1">
                    <div className="p-1.5 bg-astro-bg rounded-lg border border-astro-border print:bg-white print:border-astro-border">
                      <span className="text-[9px] text-astro-secondary-text block print:text-astro-muted">Life Stone</span>
                      <span className="font-bold text-astro-primary print:text-astro-secondary text-[11px]">{lifeStone?.gemstone || 'N/A'}</span>
                    </div>
                    <div className="p-1.5 bg-astro-bg rounded-lg border border-astro-border print:bg-white print:border-astro-border">
                      <span className="text-[9px] text-astro-secondary-text block print:text-astro-muted">Lucky Stone</span>
                      <span className="font-bold text-astro-primary print:text-astro-secondary text-[11px]">{luckyStone?.gemstone || 'N/A'}</span>
                    </div>
                    <div className="p-1.5 bg-astro-bg rounded-lg border border-astro-border print:bg-white print:border-astro-border">
                      <span className="text-[9px] text-astro-secondary-text block print:text-astro-muted">Fortune Stone</span>
                      <span className="font-bold text-astro-primary print:text-astro-secondary text-[11px]">{fortuneStone?.gemstone || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER & CERTIFICATION */}
        <div className="text-center pt-6 border-t border-astro-border text-[11px] text-astro-secondary-text print:border-astro-border print:text-astro-secondary-text">
          <p className="font-serif text-astro-primary print:text-astro-secondary font-bold text-xs mb-1">
            <span className="text-astro-accent">ॐ</span> Shubham Bhavatu • Vedanga Jyotisha Precision Vedic Astronomy & Astrological Suite
          </p>
          <p>This report is generated using exact astronomical computations based on the Lahiri Sidereal Ayanamsha.</p>
        </div>
      </div>
    </div>
  );
};
